import { ProductType } from '../constants';
import type { Product, SimpleProduct, VariationProduct } from '../model';
import type { TaxonomyTerm } from '../model';

export enum ProductSortBy {
  BestSeller = 'BestSeller',
  Latest = 'Latest',
  PriceLowToHigh = 'PriceLowToHigh',
  PriceHighToLow = 'PriceHighToLow',
}

export const getDiscountPercentage = (originalPrice: number, salePrice?: number): number => {
  if (!salePrice || salePrice >= originalPrice) return 0;
  return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
};

export const getProductEffectivePrice = (product: SimpleProduct | VariationProduct) => {
  if (product.salePrice) {
    return product.salePrice;
  }
  return product.originalPrice;
};

/**
 * Checks if a product is in stock
 * @param product - The product to check
 * @returns True if the product is in stock, false otherwise
 */
export const isProductInStock = (product: Product): boolean => {
  if (product.type === ProductType.Simple) {
    return product.status === 'instock';
  }

  // For variable products, check if any variation is in stock
  if (!product.variations || !Array.isArray(product.variations)) {
    return false;
  }

  return product.variations.some((variation) => variation.status === 'instock');
};

/**
 * Generic helper to get in-stock variation based on price sorting preference
 * @param product - The product to get the variation from
 * @param sortAscending - If true, sorts lowest to highest; if false, sorts highest to lowest
 * @returns The first in-stock variation from the sorted list or null if none available
 */
const getPriceInStockVariation = (product: Product, sortAscending: boolean): VariationProduct | null => {
  if (product.type === ProductType.Simple) {
    return null;
  }

  // Check if variations exist and is an array
  if (!product.variations || !Array.isArray(product.variations) || product.variations.length === 0) {
    return null;
  }

  // Sort all variations by price based on preference
  const sortedByPrice = product.variations.sort((a, b) => {
    const priceA = getProductEffectivePrice(a);
    const priceB = getProductEffectivePrice(b);
    return sortAscending ? priceA - priceB : priceB - priceA;
  });

  // Find the first in-stock variation from the price-sorted list
  const inStockVariation = sortedByPrice.find((variation) => variation.status === 'instock');

  return inStockVariation || null;
};

/**
 * Gets the lowest price in-stock variation for variable products
 * Uses the lowest price variant if in stock, otherwise the second lowest, etc.
 * @param product - The product to get the variation from
 * @returns The lowest price in-stock variation or null if none available
 */
export const getLowestPriceInStockVariation = (product: Product): VariationProduct | null => {
  return getPriceInStockVariation(product, true);
};

/**
 * Gets the highest price in-stock variation for variable products
 * Uses the highest price variant if in stock, otherwise the second highest, etc.
 * @param product - The product to get the variation from
 * @returns The highest price in-stock variation or null if none available
 */
export const getHighestPriceInStockVariation = (product: Product): VariationProduct | null => {
  return getPriceInStockVariation(product, false);
};

/**
 * Returns basic pricing information based on sort context
 * For simple products: returns the product's basic info
 * For variable products: returns the appropriate variant based on sortBy parameter
 * @param product - The product to get pricing info from
 * @param sortBy - Optional sort context. If 'PriceHighToLow', returns highest price variant
 * @returns Basic product information with size, pack, and pricing
 */
type PriceProductBasicInfo = {
  sku: string;
  size: string;
  pack: string;
  originalPrice: number;
  salePrice?: number;
};

export const getPriceProductBasicInfo = (
  product: Product | VariationProduct,
  sortBy?: ProductSortBy,
): PriceProductBasicInfo => {
  // A variation prices itself, exactly like a simple product — only a variable product has to
  // choose among children. Looking a variant SKU up (as the wishlist does) returns the variation.
  if (product.type === ProductType.Simple || product.type === ProductType.Variation) {
    return {
      sku: product.sku,
      size: product.size,
      pack: product.pack,
      originalPrice: product.originalPrice,
      salePrice: product.salePrice,
    };
  }

  // For variable products, get the appropriate variation based on sort context
  const shouldUseHighestPrice = sortBy === ProductSortBy.PriceHighToLow;
  const selectedVariation = shouldUseHighestPrice
    ? getHighestPriceInStockVariation(product)
    : getLowestPriceInStockVariation(product);

  if (!selectedVariation) {
    // If no instock variations, use the first variation for pricing
    if (!product.variations || !Array.isArray(product.variations) || product.variations.length === 0) {
      // Fallback values if no variations are available
      return {
        sku: product.sku,
        size: product.size,
        pack: product.pack,
        originalPrice: 0,
        salePrice: undefined,
      };
    }

    const firstVariation = product.variations[0];

    return {
      sku: firstVariation.sku,
      size: firstVariation.size,
      pack: firstVariation.pack,
      originalPrice: firstVariation.originalPrice,
      salePrice: firstVariation.salePrice,
    };
  }

  return {
    sku: selectedVariation.sku,
    size: selectedVariation.size,
    pack: selectedVariation.pack,
    originalPrice: selectedVariation.originalPrice,
    salePrice: selectedVariation.salePrice,
  };
};

export const getProductScore = (product: Product): number | undefined => {
  if (product.type === ProductType.Simple) {
    return product.score;
  }

  // Check if variations exist and is an array
  if (!product.variations || !Array.isArray(product.variations)) {
    return undefined;
  }

  const scores = product.variations
    .map((variation) => variation.score)
    .filter((score): score is number => score !== undefined);

  return scores.length > 0 ? Math.max(...scores) : undefined;
};

export const sortProductsByScore = (products: Product[]): Product[] => {
  return [...products].sort((a, b) => {
    const scoreA = getProductScore(a);
    const scoreB = getProductScore(b);

    if (scoreA === undefined && scoreB === undefined) {
      return 0;
    }
    if (scoreA === undefined) {
      return 1;
    }
    if (scoreB === undefined) {
      return -1;
    }
    return scoreB - scoreA;
  });
};

export const getProductType = (product: Product | VariationProduct): ProductType => {
  if (product.type === ProductType.Simple) {
    return ProductType.Simple;
  }
  return ProductType.Variable;
};

export const convertMlToOz = (inputString: string): string => {
  const OZ_PER_ML = 29.5735;

  if (inputString.includes('+')) {
    return inputString;
  }

  const match = inputString.match(/(\d+(?:\.\d+)?)ml/i);
  if (!match) {
    return inputString;
  }
  const mlValue = parseFloat(match[1]);

  const ozValue = mlValue / OZ_PER_ML;
  const roundedOz = Math.round(ozValue * 100) / 100;

  return `${mlValue}ml (${roundedOz}oz)`;
};

/**
 * Product size and pack converter for display purposes
 */
export interface ProductSizePackData {
  size?: string;
  pack?: string;
  color?: string;
}

export const productSizeAndPackConverter = (
  data: ProductSizePackData,
): {
  size: string | null;
  pack: string | null;
  color: string | null;
} => {
  const { size: _size, pack, color } = data;
  const size = _size ? convertMlToOz(_size) : null;

  if (pack === '1set') {
    return { size: null, pack, color: null };
  }

  // Transform pack to add space and capitalize (e.g., "1pack" -> "1 Pack")
  let formattedPack = pack ?? null;
  if (formattedPack) {
    formattedPack = formattedPack.replace(/(\d+)(pack)/i, '$1 Pack');
  }

  return {
    size,
    pack: formattedPack,
    color: color || null,
  };
};

export const productSizeAndPackTextConverter = (data: ProductSizePackData): string | null => {
  const { size, pack, color } = productSizeAndPackConverter(data);

  const parts = [size, pack, color].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
};

export const createTermIdExtractor = (taxonomyData: TaxonomyTerm[] = []) => {
  // Create a map of slug to termId for efficient lookups
  const slugToTermIdMap = new Map<string, number>();

  taxonomyData.forEach((term) => {
    slugToTermIdMap.set(term.slug, term.termId);
  });

  // Return a function that can look up a termId from a slug
  return (slug: string): number | undefined => {
    return slugToTermIdMap.get(slug);
  };
};

/**
 * Generate URL based on the selection state of the product finder dropdowns
 *
 * Implements the following rules:
 * 1. If all dropdowns are unchanged: /shop/
 * 2. If only first dropdown changed: [category URL from constants]
 * 3. If first unchanged but second/third/both changed: /shop/?filters=skin_type[termId]|skin_concern[termId]
 * 4. If first and (second or third) changed: [category URL]?filters=skin_type[termId]|skin_concern[termId]
 */

const getCategoryUrl = (slug: string): string => {
  if (!slug || slug === 'all') {
    return '/shop/';
  }
  return `/collections/${slug}/`;
};

export const buildFilterQuery = ({
  skinTypeIds,
  skinConcernIds,
}: {
  skinTypeIds?: number[];
  skinConcernIds?: number[];
}): string => {
  const filterParts: string[] = [];

  if (skinTypeIds && skinTypeIds.length > 0) {
    filterParts.push(`skin_type[${skinTypeIds.join('+')}]`);
  }

  if (skinConcernIds && skinConcernIds.length > 0) {
    filterParts.push(`skin_concern[${skinConcernIds.join('+')}]`);
  }

  if (filterParts.length === 0) {
    return '';
  }

  return `?filters=${filterParts.join('|')}`;
};

/**
 * Generate URL based on the selection state of the product finder dropdowns
 *
 * Implements the following rules:
 * 1. If all dropdowns are unchanged: /shop/
 * 2. If only first dropdown changed: [category URL from constants]
 * 3. If first unchanged but second/third/both changed: /shop/?filters=skin_type[termId]|skin_concern[termId]
 * 4. If first and (second or third) changed: [category URL]?filters=skin_type[termId]|skin_concern[termId]
 */
export const productFinderGenerateUrl = ({
  productType,
  skinType,
  skinConcern,
  getSkinTypeTermId,
  getSkinConcernTermId,
}: {
  productType: string;
  skinType: string;
  skinConcern: string;
  getSkinTypeTermId: (slug: string) => number | undefined;
  getSkinConcernTermId: (slug: string) => number | undefined;
}): string => {
  const productChanged = productType !== 'all';
  const skinTypeChanged = skinType !== 'all';
  const concernChanged = skinConcern !== 'all';

  // Case 1: No changes to any dropdown
  if (!productChanged && !skinTypeChanged && !concernChanged) {
    return '/shop/';
  }

  // Case 2: Only product category changed
  if (productChanged && !skinTypeChanged && !concernChanged) {
    return getCategoryUrl(productType);
  }

  // Case 3: Product unchanged, but skin type and/or concern changed
  if (!productChanged && (skinTypeChanged || concernChanged)) {
    const skinTypeIds: number[] = [];
    const skinConcernIds: number[] = [];

    if (skinTypeChanged) {
      const skinTypeId = getSkinTypeTermId(skinType);
      if (skinTypeId) {
        skinTypeIds.push(skinTypeId);
      }
    }

    if (concernChanged) {
      const concernId = getSkinConcernTermId(skinConcern);
      if (concernId) {
        skinConcernIds.push(concernId);
      }
    }

    if (skinTypeIds.length === 0 && skinConcernIds.length === 0) {
      return '/shop/';
    }

    return `/shop/${buildFilterQuery({ skinTypeIds, skinConcernIds })}`;
  }

  // Case 4: Product category changed AND (skin type or concern changed)
  if (productChanged && (skinTypeChanged || concernChanged)) {
    const baseUrl = getCategoryUrl(productType);
    const skinTypeIds: number[] = [];
    const skinConcernIds: number[] = [];

    if (skinTypeChanged) {
      const skinTypeId = getSkinTypeTermId(skinType);
      if (skinTypeId) {
        skinTypeIds.push(skinTypeId);
      }
    }

    if (concernChanged) {
      const concernId = getSkinConcernTermId(skinConcern);
      if (concernId) {
        skinConcernIds.push(concernId);
      }
    }

    if (skinTypeIds.length === 0 && skinConcernIds.length === 0) {
      return baseUrl;
    }

    // Ensure we're not appending a query parameter to a URL that already ends with a slash
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    return `${cleanBaseUrl}${buildFilterQuery({ skinTypeIds, skinConcernIds })}`;
  }

  return '/shop/';
};
